import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLazyInView from '../hooks/useLazyInView';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  InputAdornment,
  Pagination,
  Popover,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Fade,
  useMediaQuery,
  useTheme,
  Paper,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import {
  Add as AddIcon,
  ArrowDownward,
  ArrowDropDown,
  ArrowUpward,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Close,
  FileDownload,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  Schedule,
  AssignmentInd as AssignmentIndIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { addDays, format, isSameDay } from 'date-fns';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { AuditProcessingTimelineModal } from './AuditDialogs';
import {
  getActivityCameras,
  getActivityLogDetail,
  getActivityGroupTimeline,
  getActivityGroupCameraAnalysis,
  getActivityLogGroups,
  getAuditImageBlob,
  getFaceImage,
  getPersonFaces,
  exportActivityLogs,
  getAllPersonNames,
} from '../utils/api';
import { peek as peekAuditImage, getOrFetch as fetchAuditImage } from '../services/auditImageCache';
import useAuditImage from '../hooks/useAuditImage';
import useAuditImagePrefetch from '../hooks/useAuditImagePrefetch';
import LazyAuditImg from './LazyAuditImg';
import AuditImg from './AuditImg';
import { filterPersonNameOptions, formatPersonNameOptionLabel, sanitizeNameSearchInput } from '../utils/nameSearchInput';
import { EmployeeNameHyperlink } from './EmployeeNameHyperlink';
import { buildPersonLinkMeta } from '../utils/personDetailsNavigation';
import { employeeTypeFilterToRoleParams, groupShowsVendorBadge, groupShowsVisitorBadge, EMPLOYEE_TYPE_FILTER_DEFAULT } from '../utils/employeeTypeUi';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useImagePreview } from './ImagePreviewDialog';
import * as XLSX from 'xlsx';

const PAGE_LIMIT = 25;
const ANONYMOUS_IMAGE = '/anonymous.webp';

const LazyAuditAvatar = ({ auditId, alt, fallbackLetter, sx }) => {
  const { ref, url } = useAuditImage(auditId, { enabled: Boolean(auditId) });
  return (
    <Avatar ref={ref} src={url || undefined} alt={alt} sx={sx}>
      {!url ? fallbackLetter : null}
    </Avatar>
  );
};

const TIMELINE_START_HOUR = 8;
const TIMELINE_END_HOUR = 24;
const TIMELINE_TOTAL_MINUTES = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60;
const TIMELINE_MIN_SPAN = 30;
const TIMELINE_ZOOM_FACTOR = 1.12;
/** Full-day view (8 AM–midnight) = 100% zoom. */
const TIMELINE_FULL_DAY_WINDOW = {
  startMinute: TIMELINE_START_HOUR * 60,
  endMinute: TIMELINE_END_HOUR * 60,
};

// Shimmer animation keyframes (shared skeleton shimmer)
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shimmer effect styles
const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

/**
 * Shared palette for timeline + analysis. In/Out markers use fixed greys/orange so they never
 * steal palette slots from named activity cameras.
 */
const TIMELINE_CAMERA_LABEL_COLORS = ['#40518A', '#22B473', '#636C7A', '#F7990D', '#42bbeb'];

const IN_CAMERA_TIMELINE_COLOR = '#636C7A';
const OUT_CAMERA_TIMELINE_COLOR = '#F7990D';

/** Palette entries for labels other than fixed In/Out (avoids assigning grey/orange to activity cams). */
const ACTIVITY_TIMELINE_PALETTE = TIMELINE_CAMERA_LABEL_COLORS.filter(
  (c) => c !== IN_CAMERA_TIMELINE_COLOR && c !== OUT_CAMERA_TIMELINE_COLOR,
);

const hashStringToUint = (str) => {
  let h = 2166136261;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/** Key used for color must match analysis API `breakdown[].label` (server uses "Other" when unset). */
const cameraDisplayLabelKey = (label) => {
  const s = String(label ?? '').trim();
  return s || 'Other';
};

const CAMERA_COUNT_FIELD_PREFIX = 'camera_count__';

const buildCameraCountFieldKey = (cameraLabel) => {
  return `${CAMERA_COUNT_FIELD_PREFIX}${encodeURIComponent(cameraDisplayLabelKey(cameraLabel))}`;
};

const buildCameraCountFieldDefs = (cameraLabels = []) => {
  const unique = Array.from(new Set((cameraLabels || []).map((label) => cameraDisplayLabelKey(label))));
  unique.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return unique.map((label) => ({
    field: buildCameraCountFieldKey(label),
    label,
    cameraLabel: label,
    kind: 'cameraCount',
    enabled: true,
  }));
};

const buildTimelineCameraColorResolver = (orderedLabels = []) => {
  const colorByLabel = new Map();
  let activityPaletteIdx = 0;

  orderedLabels.forEach((label) => {
    const key = cameraDisplayLabelKey(label);
    if (colorByLabel.has(key)) {
      return;
    }
    if (key === cameraDisplayLabelKey('In Camera')) {
      colorByLabel.set(key, IN_CAMERA_TIMELINE_COLOR);
      return;
    }
    if (key === cameraDisplayLabelKey('Out Camera')) {
      colorByLabel.set(key, OUT_CAMERA_TIMELINE_COLOR);
      return;
    }
    colorByLabel.set(
      key,
      ACTIVITY_TIMELINE_PALETTE[activityPaletteIdx % ACTIVITY_TIMELINE_PALETTE.length],
    );
    activityPaletteIdx += 1;
  });

  return (label) => {
    const key = cameraDisplayLabelKey(label);
    if (colorByLabel.has(key)) {
      return colorByLabel.get(key);
    }
    if (key === cameraDisplayLabelKey('In Camera')) {
      return IN_CAMERA_TIMELINE_COLOR;
    }
    if (key === cameraDisplayLabelKey('Out Camera')) {
      return OUT_CAMERA_TIMELINE_COLOR;
    }

    const hash = hashStringToUint(key);
    const hue = hash % 360;
    const saturation = 64 + (hash % 10);
    const lightness = 44 + (hash % 8);
    return `hsl(${hue} ${saturation}% ${lightness}%)`;
  };
};

const parseAnalysisPercent = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const parsed = Number(String(value ?? '').replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAnalysisPercent = (value) => {
  const percent = parseAnalysisPercent(value);
  if (percent <= 0) return '0%';
  if (percent < 1) return `${percent.toFixed(1)}%`;
  if (Number.isInteger(percent)) return `${percent}%`;
  return `${percent.toFixed(1)}%`;
};

const buildVisibleAnalysisBarSegments = (breakdown = []) => {
  const normalized = breakdown.map((seg) => ({
    ...seg,
    numericPercent: Math.max(0, parseAnalysisPercent(seg?.percent)),
    numericCount: Math.max(0, Number(seg?.count) || 0),
    displayPercent: 0,
  }));

  const positive = normalized.filter((seg) => seg.numericPercent > 0 || seg.numericCount > 0);
  if (!positive.length) {
    return normalized;
  }

  if (positive.every((seg) => seg.numericPercent <= 0)) {
    const equalPercent = 100 / positive.length;
    return normalized.map((seg) => (
      positive.includes(seg)
        ? { ...seg, displayPercent: equalPercent }
        : seg
    ));
  }

  let minVisiblePercent = 0.75;
  if ((positive.length * minVisiblePercent) >= 100) {
    minVisiblePercent = 100 / positive.length;
  }

  positive.forEach((seg) => {
    seg.displayPercent = Math.max(seg.numericPercent, minVisiblePercent);
  });

  let totalDisplayPercent = positive.reduce((sum, seg) => sum + seg.displayPercent, 0);
  if (totalDisplayPercent > 100) {
    const surplus = totalDisplayPercent - 100;
    const adjustable = positive.filter((seg) => seg.displayPercent > minVisiblePercent);
    const adjustableRoom = adjustable.reduce((sum, seg) => sum + (seg.displayPercent - minVisiblePercent), 0);

    if (adjustableRoom > 0) {
      adjustable.forEach((seg) => {
        const room = seg.displayPercent - minVisiblePercent;
        seg.displayPercent -= (surplus * room) / adjustableRoom;
      });
    }
  }

  totalDisplayPercent = positive.reduce((sum, seg) => sum + seg.displayPercent, 0);
  if (totalDisplayPercent < 100) {
    const largest = positive.reduce((maxSeg, seg) => (
      !maxSeg || seg.displayPercent > maxSeg.displayPercent ? seg : maxSeg
    ), null);
    if (largest) {
      largest.displayPercent += 100 - totalDisplayPercent;
    }
  }

  return normalized;
};

const defaultDateRange = () => ([
  { startDate: new Date(), endDate: new Date(), key: 'selection' },
]);

const parseDateParam = (value) => {
  if (!value) return null;
  const parsed = new Date(`${String(value).trim()}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeTimeParam = (value, fallback) => {
  const raw = String(value || '').trim();
  if (!/^\d{2}:\d{2}$/.test(raw)) return fallback;
  const [hours, minutes] = raw.split(':').map(Number);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return fallback;
  return raw;
};

const buildDateTimeString = (dateValue, timeValue, fallbackTime) => {
  const baseDate = dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
    ? dateValue
    : new Date();
  const [hours, minutes] = normalizeTimeParam(timeValue, fallbackTime).split(':').map(Number);
  const next = new Date(baseDate);
  next.setHours(hours, minutes, fallbackTime === '23:59' ? 59 : 0, 0);
  return format(next, 'yyyy-MM-dd HH:mm:ss');
};

const parseActivityTimestamp = (value) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const input = String(value).trim();
  if (!input) return null;
  if (/^\d+$/.test(input)) {
    const num = Number(input);
    const parsed = new Date(num > 1e12 ? num : num * 1000);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/i.test(input);
  let normalized = input;
  if (!hasTimezone && /^\d{4}-\d{2}-\d{2} \d/.test(input)) {
    normalized = input.replace(' ', 'T');
  }
  const parsed = new Date(hasTimezone ? input : `${normalized}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatShortTime = (value) => {
  if (!value) return '-';
  const dt = parseActivityTimestamp(value);
  if (!dt) return '-';
  if (Number.isNaN(dt.getTime())) return '-';
  return format(dt, 'hh:mm a');
};

const formatDayLabel = (value) => {
  if (!value) return 'Unknown Date';
  const dt = parseActivityTimestamp(value);
  if (!dt) return 'Unknown Date';
  if (Number.isNaN(dt.getTime())) return 'Unknown Date';
  return format(dt, 'EEE, MMM dd, yyyy');
};

const ACTIVITY_EXPORT_FIELDS = [
  { field: 'recognized_name', label: 'Name', enabled: true },
  { field: 'recognized_person_id', label: 'Person ID', enabled: true },
  { field: 'employee_code', label: 'Employee Code', enabled: true },
  { field: 'day', label: 'Date', enabled: true },
];

/** Minutes from local midnight with sub-minute precision (stable timeline order). */
const eventTimeToTimelineMinutes = (value) => {
  const dt = parseActivityTimestamp(value);
  if (!dt || Number.isNaN(dt.getTime())) return TIMELINE_START_HOUR * 60;
  return (
    dt.getHours() * 60
    + dt.getMinutes()
    + dt.getSeconds() / 60
    + dt.getMilliseconds() / 60000
  );
};

const compareEventTimesAsc = (a, b) => {
  const at = parseActivityTimestamp(a?.event_time)?.getTime();
  const bt = parseActivityTimestamp(b?.event_time)?.getTime();
  const av = Number.isFinite(at) ? at : 0;
  const bv = Number.isFinite(bt) ? bt : 0;
  if (av !== bv) return av - bv;
  const aid = String(a?.activity_id ?? '');
  const bid = String(b?.activity_id ?? '');
  return aid.localeCompare(bid);
};

const ActivityTimelineBar = ({
  events = [],
  snapshotCache = {},
  height = 42,
  compact = false,
  colorForCameraLabel = () => TIMELINE_CAMERA_LABEL_COLORS[0],
  onMarkerClick = null,
  onRequestAuditImage = null,
}) => {
  const timelineRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startMinute: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [zoomWindow, setZoomWindow] = useState(TIMELINE_FULL_DAY_WINDOW);

  const sortedEvents = useMemo(() => [...events].sort(compareEventTimesAsc), [events]);
  const visibleSpan = zoomWindow.endMinute - zoomWindow.startMinute;
  const isZoomed = visibleSpan < TIMELINE_TOTAL_MINUTES;

  const clampWindow = useCallback((startMinute, endMinute) => {
    const fullStart = TIMELINE_START_HOUR * 60;
    const fullEnd = TIMELINE_END_HOUR * 60;
    const span = Math.max(TIMELINE_MIN_SPAN, Math.min(TIMELINE_TOTAL_MINUTES, endMinute - startMinute));
    let nextStart = startMinute;
    let nextEnd = nextStart + span;

    if (nextStart < fullStart) {
      nextStart = fullStart;
      nextEnd = fullStart + span;
    }
    if (nextEnd > fullEnd) {
      nextEnd = fullEnd;
      nextStart = fullEnd - span;
    }
    return { startMinute: nextStart, endMinute: nextEnd };
  }, []);

  // Always open at 100% (full day). Reset when the day's events change.
  useEffect(() => {
    setZoomWindow(TIMELINE_FULL_DAY_WINDOW);
  }, [sortedEvents]);

  const getPositionPercent = useCallback((value) => {
    const minuteValue = eventTimeToTimelineMinutes(value);
    const clamped = Math.max(zoomWindow.startMinute, Math.min(zoomWindow.endMinute, minuteValue));
    return ((clamped - zoomWindow.startMinute) / visibleSpan) * 100;
  }, [visibleSpan, zoomWindow.endMinute, zoomWindow.startMinute]);

  const hourMarks = useMemo(() => {
    return Array.from({ length: TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1 }, (_, idx) => {
      const hour = TIMELINE_START_HOUR + idx;
      const minute = hour * 60;
      if (minute < zoomWindow.startMinute || minute > zoomWindow.endMinute) return null;
      const labelHour = hour === 24 ? 12 : hour > 12 ? hour - 12 : hour;
      const suffix = hour >= 12 && hour < 24 ? 'PM' : 'AM';
      return {
        key: hour,
        label: `${labelHour} ${suffix}`,
        left: `${((minute - zoomWindow.startMinute) / visibleSpan) * 100}%`,
      };
    }).filter(Boolean);
  }, [visibleSpan, zoomWindow.endMinute, zoomWindow.startMinute]);

  const displayHourMarks = useMemo(() => {
    if (!isZoomed) return hourMarks;

    let stepMinutes = 60;
    if (visibleSpan <= 30) stepMinutes = 5;
    else if (visibleSpan <= 60) stepMinutes = 10;
    else if (visibleSpan <= 120) stepMinutes = 15;
    else if (visibleSpan <= 240) stepMinutes = 30;

    const marks = [];
    const roundedStart = Math.floor(zoomWindow.startMinute / stepMinutes) * stepMinutes;

    for (let minute = roundedStart; minute <= zoomWindow.endMinute; minute += stepMinutes) {
      if (minute < zoomWindow.startMinute) continue;
      const hour = Math.floor(minute / 60);
      const mins = minute % 60;
      const labelHour = hour === 24 ? 12 : hour > 12 ? hour - 12 : hour;
      const suffix = hour >= 12 && hour < 24 ? 'PM' : 'AM';
      const label = stepMinutes >= 60
        ? `${labelHour} ${suffix}`
        : `${labelHour}:${String(mins).padStart(2, '0')} ${suffix}`;

      marks.push({
        key: `${hour}-${mins}`,
        label,
        left: `${((minute - zoomWindow.startMinute) / visibleSpan) * 100}%`,
      });
    }

    return marks;
  }, [hourMarks, isZoomed, visibleSpan, zoomWindow.endMinute, zoomWindow.startMinute]);

  const markerItems = useMemo(() => {
    const baseMarkers = sortedEvents
      .map((event, idx) => {
        const minute = eventTimeToTimelineMinutes(event.event_time);
        if (isZoomed && (minute < zoomWindow.startMinute || minute > zoomWindow.endMinute)) return null;
        return {
          id: event.activity_id || `${event.audit_id || 'audit'}-${idx}`,
          minute,
          position: isZoomed
            ? ((minute - zoomWindow.startMinute) / visibleSpan) * 100
            : getPositionPercent(event.event_time),
          event,
          image: event.audit_id ? snapshotCache[event.audit_id] : null,
        };
      })
      .filter(Boolean);

    /*
     * Horizontal pixel offsets for "crowded" markers used to fan dots ±60px around a
     * cluster center, which could move a later event left of an earlier one (e.g.
     * 7:02 PM visually before 6:18 PM). Use true time-derived `position` only (offset 0).
     * Dense stacks overlap; zoom narrows the window to separate them.
     */
    return baseMarkers.map((marker) => ({
      ...marker,
      offsetXPx: 0,
    }));
  }, [getPositionPercent, isZoomed, snapshotCache, sortedEvents, visibleSpan, zoomWindow.endMinute, zoomWindow.startMinute]);

  /**
   * Tinted spans on the timeline rail: between consecutive dots (left dot's camera color),
   * plus edge spans (one dot → tint full visible width; many dots → tint 0→first and last→100).
   */
  const timelineInterMarkerSegments = useMemo(() => {
    if (!markerItems.length) {
      return [];
    }
    const ordered = [...markerItems].sort((a, b) => {
      if (a.minute !== b.minute) return a.minute - b.minute;
      return String(a.id).localeCompare(String(b.id));
    });
    const out = [];
    const opacity = compact ? 0.36 : 0.42;
    const minW = 0.05;

    const pushSeg = (key, leftPct, widthPct, event) => {
      if (!(widthPct > minW)) return;
      const baseColor = colorForCameraLabel(event?.camera_label) || TIMELINE_CAMERA_LABEL_COLORS[0];
      out.push({
        key,
        leftPct,
        widthPct,
        fill: alpha(baseColor, opacity),
      });
    };

    if (ordered.length === 1) {
      pushSeg(`seg-full-${ordered[0].id}`, 0, 100, ordered[0].event);
      return out;
    }

    const first = ordered[0];
    const last = ordered[ordered.length - 1];
    if (first.position > minW) {
      pushSeg(`seg-lead-${first.id}`, 0, first.position, first.event);
    }
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const left = ordered[i];
      const right = ordered[i + 1];
      const widthPct = right.position - left.position;
      if (!(widthPct > minW)) {
        continue;
      }
      pushSeg(`seg-${left.id}-${right.id}`, left.position, widthPct, left.event);
    }
    if (last.position < 100 - minW) {
      pushSeg(`seg-trail-${last.id}`, last.position, 100 - last.position, last.event);
    }
    return out;
  }, [markerItems, colorForCameraLabel, compact]);

  const handleWheelZoom = useCallback((event) => {
    if (!timelineRef.current) return;
    event.preventDefault();
    const rect = timelineRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const pointerRatio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const pointerMinute = zoomWindow.startMinute + (pointerRatio * visibleSpan);
    const nextSpan = event.deltaY < 0
      ? Math.max(TIMELINE_MIN_SPAN, visibleSpan / TIMELINE_ZOOM_FACTOR)
      : Math.min(TIMELINE_TOTAL_MINUTES, visibleSpan * TIMELINE_ZOOM_FACTOR);
    const nextStart = pointerMinute - (pointerRatio * nextSpan);
    setZoomWindow(clampWindow(nextStart, nextStart + nextSpan));
  }, [clampWindow, visibleSpan, zoomWindow.startMinute]);

  const handlePointerDown = useCallback((event) => {
    if (!isZoomed) return;
    panRef.current = {
      active: true,
      startX: event.clientX,
      startMinute: zoomWindow.startMinute,
    };
    setIsPanning(true);
  }, [isZoomed, zoomWindow.startMinute]);

  const handlePointerMove = useCallback((event) => {
    if (!panRef.current.active || !timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    if (!rect.width) return;
    const deltaX = event.clientX - panRef.current.startX;
    const deltaMinutes = (deltaX / rect.width) * visibleSpan;
    const nextStart = panRef.current.startMinute - deltaMinutes;
    setZoomWindow(clampWindow(nextStart, nextStart + visibleSpan));
  }, [clampWindow, visibleSpan]);

  useEffect(() => {
    const stopPanning = () => {
      panRef.current.active = false;
      setIsPanning(false);
    };
    window.addEventListener('pointerup', stopPanning);
    return () => window.removeEventListener('pointerup', stopPanning);
  }, []);

  const handleZoomStep = (direction) => {
    const pointerMinute = (zoomWindow.startMinute + zoomWindow.endMinute) / 2;
    const nextSpan = direction === 'in'
      ? Math.max(TIMELINE_MIN_SPAN, visibleSpan / TIMELINE_ZOOM_FACTOR)
      : Math.min(TIMELINE_TOTAL_MINUTES, visibleSpan * TIMELINE_ZOOM_FACTOR);
    const nextStart = pointerMinute - (nextSpan / 2);
    setZoomWindow(clampWindow(nextStart, nextStart + nextSpan));
  };

  const handlePanStep = (direction) => {
    const shift = visibleSpan * 0.2 * direction;
    setZoomWindow(clampWindow(zoomWindow.startMinute + shift, zoomWindow.endMinute + shift));
  };
  const timelineHeight = compact ? 36 : Math.max(height, 36);
  const timelineLabelStripHeight = compact ? 14 : 20;
  const trackCenterTop = (timelineHeight - timelineLabelStripHeight) / 2;

  return (
    <Box>
      {!compact ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Timeline
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              px: 0.4,
              py: 0.2,
              borderRadius: 2,
              border: '1px solid #cbd5e1',
              bgcolor: '#f8fafc',
            }}
          >
            <IconButton size="small" onClick={() => handleZoomStep('out')} disabled={visibleSpan >= TIMELINE_TOTAL_MINUTES} sx={{ width: 26, height: 26, borderRadius: 1.5 }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Chip
              size="small"
              label={`${Math.round((TIMELINE_TOTAL_MINUTES / visibleSpan) * 100)}%`}
              sx={{ height: 24, minWidth: 58, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#0f172a' }}
            />
            <IconButton size="small" onClick={() => handleZoomStep('in')} disabled={visibleSpan <= TIMELINE_MIN_SPAN} sx={{ width: 26, height: 26, borderRadius: 1.5 }}>
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setZoomWindow(TIMELINE_FULL_DAY_WINDOW)} disabled={!isZoomed} sx={{ width: 26, height: 26, borderRadius: 1.5 }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      ) : null}
      <Box sx={{ position: 'relative', px: 0.5 }}>
        <Box
          ref={timelineRef}
          onWheel={handleWheelZoom}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onDoubleClick={() => setZoomWindow(TIMELINE_FULL_DAY_WINDOW)}
          sx={{
            position: 'relative',
            height: timelineHeight,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #dbe3ee',
            cursor: isZoomed ? (isPanning ? 'grabbing' : 'grab') : 'default',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: trackCenterTop,
              left: 0,
              right: 0,
              height: compact ? 10 : 12,
              transform: 'translateY(-50%)',
              bgcolor: '#e1e2e6',
              borderRadius: 999,
            }}
          />
          {timelineInterMarkerSegments.map((seg) => (
            <Box
              key={seg.key}
              sx={{
                position: 'absolute',
                left: `${seg.leftPct}%`,
                top: trackCenterTop,
                transform: 'translateY(-50%)',
                width: `${seg.widthPct}%`,
                height: compact ? 10 : 12,
                bgcolor: seg.fill,
                borderRadius: 6,
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          ))}
          {displayHourMarks.map((mark) => (
            <Box
              key={mark.key}
              sx={{
                position: 'absolute',
                left: mark.left,
                top: compact ? 5 : 4,
                bottom: compact ? 14 : 18,
                width: 1,
                // bgcolor: '#e1e2e6',
                zIndex: 2,
              }}
            />
          ))}
          {markerItems.map(({ id, event, image, position, offsetXPx }) => {
            return (
              <Tooltip
                key={id}
                arrow
                onOpen={() => {
                  if (event?.audit_id && typeof onRequestAuditImage === 'function') {
                    onRequestAuditImage(event.audit_id);
                  }
                }}
                title={
                  <Box sx={{ minWidth: 168, maxWidth: 188 }}>
                    {event?.audit_id ? (
                      <AuditImg
                        auditId={event.audit_id}
                        alt={event.person_name || 'Activity'}
                        sx={{
                          width: '100%',
                          height: 108,
                          borderRadius: 1.25,
                          mb: 0.75,
                          border: '1px solid rgba(148, 163, 184, 0.45)',
                          bgcolor: '#0f172a',
                          overflow: 'hidden',
                        }}
                        imgSx={{ width: '100%', height: 108, objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={image || ANONYMOUS_IMAGE}
                        alt={event.person_name || 'Activity'}
                        sx={{
                          width: '100%',
                          height: 108,
                          objectFit: 'cover',
                          borderRadius: 1.25,
                          mb: 0.75,
                          border: '1px solid rgba(148, 163, 184, 0.45)',
                          bgcolor: '#0f172a',
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#f8fafc', lineHeight: 1.15, minWidth: 0, flex: 1 }} noWrap>
                        {event.camera_label || 'Activity Camera'}
                      </Typography>
                      {Boolean(event?.is_violation) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 500,
                            lineHeight: 1.15,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Violation
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', mt: 0.2, lineHeight: 1.15 }} noWrap>
                      {formatShortTime(event.event_time)}
                    </Typography>
                  </Box>
                }
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'rgba(15, 23, 42, 0.94)',
                      border: '1px solid rgba(148, 163, 184, 0.4)',
                      boxShadow: '0 10px 24px rgba(2, 6, 23, 0.4)',
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.9,
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'rgba(15, 23, 42, 0.94)',
                    },
                  },
                }}
              >
                <Box
                  onPointerEnter={() => {
                    if (event?.audit_id && typeof onRequestAuditImage === 'function') {
                      onRequestAuditImage(event.audit_id);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    left: `calc(${position}% + ${offsetXPx}px)`,
                    top: trackCenterTop,
                    width: compact ? 10 : 14,
                    height: compact ? 10 : 14,
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: '1px solid #fff',
                    boxShadow: compact ? '0 2px 8px rgba(15, 23, 42, 0.2)' : '0 2px 6px rgba(15, 23, 42, 0.22)',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 3,
                    bgcolor: colorForCameraLabel(event?.camera_label),
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    cursor: typeof onMarkerClick === 'function' ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translate(-50%, -50%) scale(1.08)',
                      boxShadow: compact ? '0 3px 10px rgba(15, 23, 42, 0.28)' : '0 4px 10px rgba(15, 23, 42, 0.28)',
                    },
                  }}
                  onClick={(mouseEvent) => {
                    if (typeof onMarkerClick === 'function') {
                      mouseEvent.stopPropagation();
                      onMarkerClick(event, image || null);
                    }
                  }}
                >
                  {Boolean(event?.is_violation) ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        width: compact ? 3.5 : 5,
                        height: compact ? 3.5 : 5,
                        borderRadius: '50%',
                        bgcolor: '#dc2626',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}
                </Box>
              </Tooltip>
            );
          })}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: compact ? 14 : 20,
              bgcolor: 'rgba(255,255,255,0.88)',
              borderTop: '1px solid rgba(148, 163, 184, 0.22)',
            }}
          >
            {displayHourMarks.map((mark, idx) => {
              const isMobileLabel = typeof window !== 'undefined' && window.innerWidth < 600;
              const skipOnMobile = isMobileLabel && idx % 2 !== 0 && idx !== 0 && idx !== displayHourMarks.length - 1;
              if (skipOnMobile) return null;
              const isFirst = idx === 0;
              const isLast = idx === displayHourMarks.length - 1;
              return (
                <Typography
                  key={`${mark.key}-label`}
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: mark.left,
                    bottom: 1,
                    transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                    color: '#64748b',
                    fontSize: compact ? '0.5rem' : '0.6rem',
                    whiteSpace: 'nowrap',
                    px: 0.25,
                  }}
                >
                  {mark.label}
                </Typography>
              );
            })}
          </Box>
        </Box>
        {isZoomed && !compact ? (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 0.75 }}>
            <IconButton size="small" onClick={() => handlePanStep(-1)} sx={{ bgcolor: 'rgba(255,255,255,0.9)', border: '1px solid #e5e7eb' }}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem' }}>
              Drag left or right to adjust timeline
            </Typography>
            <IconButton size="small" onClick={() => handlePanStep(1)} sx={{ bgcolor: 'rgba(255,255,255,0.9)', border: '1px solid #e5e7eb' }}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
};

const ActivitySummaryCard = ({ group, thumbnail, snapshotCache, onOpenTimeline, onOpenDetail = null, onBecomeVisible = null }) => {
  const previewEvents = Array.isArray(group?.recent_events) ? group.recent_events.slice(0, 3) : [];
  const avatarLetter = (group?.person_name || group?.person_id || '?').charAt(0).toUpperCase();
  const lazyRef = useLazyInView(() => {
    if (typeof onBecomeVisible === 'function') {
      onBecomeVisible(group);
    }
  }, { once: true });
  const getCameraName = (event) => (
    event?.camera_label ||
    event?.camera_name ||
    event?.camera_id ||
    'Unknown Camera'
  );

  return (
    <Card
      ref={lazyRef}
      variant="outlined"
      sx={{
        borderRadius: 3,
        width: '100%',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        borderTop: '3px solid #40518A',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: '0 20px 40px rgba(15,23,42,0.15)',
          transform: 'translateY(-4px)',
          borderColor: '#40518A',
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        onClick={() => onOpenTimeline(group)}
        sx={{ height: '100%', alignItems: 'stretch' }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.25, sm: 1.5 },
            height: '100%',
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center" sx={{ minWidth: 0 }}>
            {thumbnail ? (
              <Avatar
                src={thumbnail}
                alt={group?.person_name || 'Unknown person'}
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  bgcolor: '#EEF2FF',
                  color: '#374151',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {avatarLetter}
              </Avatar>
            ) : group?.latest_audit_id ? (
              <LazyAuditAvatar
                auditId={group.latest_audit_id}
                alt={group?.person_name || 'Unknown person'}
                fallbackLetter={avatarLetter}
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  bgcolor: '#EEF2FF',
                  color: '#374151',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Avatar
                alt={group?.person_name || 'Unknown person'}
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  bgcolor: '#EEF2FF',
                  color: '#374151',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {avatarLetter}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' }, minWidth: 0 }} noWrap>
                  <EmployeeNameHyperlink
                    text={group?.person_name || 'Unknown person'}
                    employees={[buildPersonLinkMeta(group, { routeSource: 'event' })]}
                    style={{ display: 'inline' }}
                  />
                </Typography>
                {groupShowsVendorBadge(group) ? (
                  <Tooltip title="Vendor" arrow placement="top">
                    <AssignmentIndIcon sx={{ fontSize: '1.05rem', color: '#40518A', flexShrink: 0 }} />
                  </Tooltip>
                ) : null}
                {groupShowsVisitorBadge(group) ? (
                  <Tooltip title="Visitor" arrow placement="top">
                    <BadgeIcon sx={{ fontSize: '1.05rem', color: '#40518A', flexShrink: 0 }} />
                  </Tooltip>
                ) : null}
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between" >
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {group?.employee_code || group?.person_id || 'Unknown'}
                </Typography>
                {group?.last_seen ? (
                    <Chip
                      size="small"
                      icon={<Schedule sx={{ fontSize: 14 }} />}
                      label={formatShortTime(group.last_seen)}
                      sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, bgcolor: '#fff' }}
                    />
                  ) : null}
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                <Chip
                  size="small"
                  label={`${group?.record_count || 0} detections`}
                  sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}
                />
                {Number(group?.violation_count || 0) > 0 ? (
                  <Chip
                    size="small"
                    label={`${group?.violation_count || 0} violations`}
                    sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Divider />

          <Box sx={{ mt: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Recent events
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {previewEvents.length ? `${previewEvents.length} shown` : 'No events'}
              </Typography>
            </Stack>
            {previewEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No activity or attendance in this range.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1.2,
                  alignItems: 'start',
                }}
              >
                {previewEvents.map((event, idx) => {
                  const image = event?.audit_id ? peekAuditImage(event.audit_id) : null;
                  return (
                    <Box
                      key={event?.activity_id || `${group.group_key}-${idx}`}
                      sx={{
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: 2,
                        p: 0.75,
                        bgcolor: '#fff',
                        cursor: typeof onOpenDetail === 'function' ? 'pointer' : 'default',
                      }}
                      onClick={(clickEvent) => {
                        if (typeof onOpenDetail === 'function') {
                          clickEvent.stopPropagation();
                          onOpenDetail(event, image || null, group);
                        }
                      }}
                    >
                      <LazyAuditImg
                        auditId={event?.audit_id}
                        alt={group?.person_name || 'Activity'}
                        anonymousImage={ANONYMOUS_IMAGE}
                        sx={{
                          width: '100%',
                          height: 96,
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: '#0f172a',
                          mb: 0.75,
                          border: '1px solid #e2e8f0',
                          flexShrink: 0,
                        }}
                        imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <Box sx={{ minHeight: 38 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {formatShortTime(event?.event_time)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600, mt: 0.1 }}
                          noWrap
                        >
                          {getCameraName(event)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const ActivitySummaryCardSkeleton = () => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderTop: '3px solid #40518A',
      boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
      overflow: 'hidden',
    }}
  >
    <CardContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, sm: 1.5 },
        height: '100%',
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: '50%',
            flexShrink: 0,
            ...shimmerStyles,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ width: '62%', height: 18, borderRadius: 1, mb: 0.75, ...shimmerStyles }} />
          <Box sx={{ width: '42%', height: 12, borderRadius: 1, ...shimmerStyles }} />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Box sx={{ width: 110, height: 24, borderRadius: 12, ...shimmerStyles }} />
            <Box sx={{ width: 86, height: 24, borderRadius: 12, ...shimmerStyles }} />
          </Stack>
        </Box>
      </Stack>

      <Divider />

      <Box sx={{ mt: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ width: 120, height: 12, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 64, height: 12, borderRadius: 1, ...shimmerStyles }} />
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1.2,
            alignItems: 'start',
          }}
        >
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                border: '1.5px solid #cbd5e1',
                borderRadius: 2,
                p: 0.75,
                bgcolor: '#fff',
              }}
            >
              <Box sx={{ width: '100%', height: 96, borderRadius: 2, mb: 0.75, ...shimmerStyles }} />
              <Box sx={{ minHeight: 38 }}>
                <Box sx={{ width: '72%', height: 10, borderRadius: 1, mb: 0.6, ...shimmerStyles }} />
                <Box sx={{ width: '88%', height: 10, borderRadius: 1, ...shimmerStyles }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const ActivityTimelineInline = ({
  open,
  onClose,
  group,
  events,
  loading,
  snapshotCache,
  thumbnail,
  onOpenEventDetail = null,
  onRequestAuditImage = null,
  /** Full event count for the selected range (matches card); not the current page length. */
  totalCount = 0,
  analysis = null,
  analysisLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const groupedDays = useMemo(() => {
    const map = new Map();
    (events || []).forEach((event) => {
      const dt = parseActivityTimestamp(event?.event_time);
      const dateKey = !dt || Number.isNaN(dt.getTime()) ? String(event?.event_time || '') : format(dt, 'yyyy-MM-dd');
      const dateLabel = formatDayLabel(event?.event_time);
      if (!map.has(dateKey)) map.set(dateKey, { dateKey, dateLabel, events: [] });
      map.get(dateKey).events.push(event);
    });
    return Array.from(map.values())
      .map((day) => ({
        ...day,
        events: [...day.events].sort(compareEventTimesAsc),
      }))
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [events]);

  /** API returns analysis days separately; join by date so timeline and analysis display together. */
  const analysisDaysByDate = useMemo(() => {
    const map = new Map();
    (analysis?.days || []).forEach((day) => {
      const dateKey = String(day?.date || '').trim();
      if (dateKey) {
        map.set(dateKey, day);
      }
    });
    return map;
  }, [analysis?.days]);

  const combinedDays = useMemo(() => {
    const merged = new Map();

    groupedDays.forEach((day) => {
      merged.set(day.dateKey, {
        dateKey: day.dateKey,
        dateLabel: day.dateLabel,
        events: day.events,
        analysisDay: analysisDaysByDate.get(day.dateKey) || null,
      });
    });

    analysisDaysByDate.forEach((analysisDay, dateKey) => {
      if (!merged.has(dateKey)) {
        merged.set(dateKey, {
          dateKey,
          dateLabel: formatDayLabel(dateKey),
          events: [],
          analysisDay,
        });
      }
    });

    return Array.from(merged.values()).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [analysisDaysByDate, groupedDays]);

  const timelineLegendLabels = useMemo(() => {
    const labels = new Map();
    const push = (raw) => {
      const label = String(raw ?? '').trim();
      if (!label || labels.has(label)) {
        return;
      }
      labels.set(label, label);
    };
    combinedDays.forEach((day) => {
      (day?.analysisDay?.breakdown || []).forEach((segment) => {
        push(segment?.label);
      });
    });
    combinedDays.forEach((day) => {
      (day?.events || []).forEach((ev) => push(ev?.camera_label));
    });
    return Array.from(labels.values());
  }, [combinedDays]);

  const colorForTimelineCameraDisplay = useMemo(
    () => buildTimelineCameraColorResolver(timelineLegendLabels),
    [timelineLegendLabels],
  );

  const daysWithEvents = useMemo(
    () => combinedDays.filter((day) => Array.isArray(day?.events) && day.events.length > 0),
    [combinedDays]
  );

  const hasAnyDayData = daysWithEvents.length > 0;

  const renderAnalysisDay = useCallback((day) => {
    if (analysisLoading) {
      return (
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Skeleton variant="rounded" height={14} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" height={14} width="85%" sx={{ borderRadius: 1 }} />
        </Stack>
      );
    }

    if (!day?.analysisDay) {
      return (
        <Typography variant="body2" color="text.secondary">
          No breakdown for this day
        </Typography>
      );
    }

    const analysisDay = day.analysisDay;
    const breakdown = Array.isArray(analysisDay.breakdown) ? analysisDay.breakdown : [];
    const barSegments = buildVisibleAnalysisBarSegments(breakdown);

    return !breakdown.length ? (
      <Typography variant="body2" color="text.secondary">No events for this day</Typography>
    ) : (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
          {typeof analysisDay.total_events === 'number' ? `${analysisDay.total_events} events analyzed` : 'Activity breakdown'}
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1, rowGap: 1, mb: 1.25, alignItems: 'stretch' }}
        >
          {breakdown.map((seg) => {
            const percentLabel = formatAnalysisPercent(seg.percent);
            const labelText = String(seg.label || '').trim() || 'Other';
            const estimatedWidth = Math.min(360, Math.max(132, (labelText.length * 8) + 88));
            return (
              <Stack
                key={seg.label}
                direction="row"
                alignItems="center"
                spacing={0}
                sx={{
                  maxWidth: '100%',
                  minHeight: 34,
                  px: 1.2,
                  py: 0.65,
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.32)',
                  bgcolor: '#f8fafc',
                  boxSizing: 'border-box',
                  width: 'fit-content',
                  minWidth: { xs: '100%', sm: Math.min(220, estimatedWidth) },
                }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    columnGap: 0.5,
                    rowGap: 0.15,
                    width: '100%',
                    minWidth: 0,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.35,
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: '#334155',
                      fontWeight: 700,
                      maxWidth: '100%',
                    }}
                  >
                    {labelText}:
                  </Box>
                  <Box component="span" sx={{ color: '#475569', fontWeight: 600 }}>
                    {percentLabel}
                  </Box>
                  <Box component="span" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    ({seg.count})
                  </Box>
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Box
          sx={{
            px: '2px',
            py: '3px',
            bgcolor: '#fff',
            borderRadius: 1.25,
            boxSizing: 'border-box',
            boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.35)',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: 16,
              borderRadius: 0.75,
              overflow: 'hidden',
              bgcolor: '#fff',
            }}
          >
            {barSegments.map((seg, index) => {
              const numericPercent = parseAnalysisPercent(seg.percent);
              const displayPercent = Math.max(0, Number(seg.displayPercent) || 0);
              const percentLabel = formatAnalysisPercent(seg.percent);
              const hasValue = numericPercent > 0 || Number(seg.count) > 0;
              return (
                <Tooltip key={`bar-${analysisDay.date}-${seg.label}`} title={`${seg.label} — ${seg.count} (${percentLabel})`} placement="top">
                  <Box
                    sx={{
                      flex: `0 0 ${displayPercent}%`,
                      minWidth: 0,
                      boxSizing: 'border-box',
                      bgcolor: colorForTimelineCameraDisplay(seg.label),
                      boxShadow: index > 0 ? 'inset 2px 0 0 #fff' : 'none',
                      opacity: hasValue ? 1 : 0,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </>
    );
  }, [analysisLoading, colorForTimelineCameraDisplay]);

  const totalDetections = useMemo(() => {
    if (typeof totalCount === 'number' && totalCount > 0) {
      return totalCount;
    }
    const cardTotal = typeof group?.record_count === 'number' ? group.record_count : 0;
    if (cardTotal > 0) {
      return cardTotal;
    }
    return events?.length || 0;
  }, [totalCount, group?.record_count, events]);
  const violationCount = useMemo(
    () => (events || []).reduce((sum, ev) => sum + (ev?.is_violation ? 1 : 0), 0),
    [events]
  );
  const dayCount = daysWithEvents.length;
  const headingDateLabel = useMemo(() => {
    if (!combinedDays.length) return '-';
    if (combinedDays.length === 1) return combinedDays[0].dateLabel;
    const latest = combinedDays[0]?.dateLabel;
    const oldest = combinedDays[combinedDays.length - 1]?.dateLabel;
    return `${oldest} - ${latest}`;
  }, [combinedDays]);

  return (
    <Box sx={{ bgcolor: '#fafbfc', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderRadius: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Detections: ${totalDetections}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
          {violationCount > 0 ? (
            <Chip size="small" label={`Violations: ${violationCount}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
          ) : null}
          <Chip size="small" label={`Days: ${dayCount}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#fef9c3', color: '#854d0e' }} />
          <Chip size="small" label={`Last Seen: ${group?.last_seen ? formatShortTime(group.last_seen) : '-'}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fafbfc' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TIMELINE_CAMERA_LABEL_COLORS[0] }} />
          </Stack>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
            {timelineLegendLabels.map((label) => (
              <Stack key={label} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 6, sm: 8 },
                    height: { xs: 6, sm: 8 },
                    bgcolor: colorForTimelineCameraDisplay(label),
                    borderRadius: '50%',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}
                >
                  {label}
                </Typography>
              </Stack>
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 22, sm: 28 }, height: { xs: 10, sm: 14 }, bgcolor: '#e5e7eb', borderRadius: 999 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Timeline</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: '#cbd5e1', border: '2px solid #fff' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Image Marker</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: '#94a3b8', border: '2px solid #fff', position: 'relative' }}>
                <Box sx={{ position: 'absolute', width: { xs: 4, sm: 6 }, height: { xs: 4, sm: 6 }, borderRadius: '50%', bgcolor: '#dc2626', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Violation</Typography>
            </Stack>
          </Stack>
        </Stack>
        {hasAnyDayData ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
            Hover a timeline dot to load its recognition snapshot; click for full details.
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !hasAnyDayData ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">No activity recorded for this period</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
            {daysWithEvents.map((day, idx) => (
              <Box key={day.dateKey}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#1f2937', mb: 1.25, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                >
                  {day.dateLabel}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <ActivityTimelineBar
                    events={day.events}
                    snapshotCache={snapshotCache}
                    colorForCameraLabel={colorForTimelineCameraDisplay}
                    onRequestAuditImage={onRequestAuditImage}
                    onMarkerClick={(event, image) => {
                      if (typeof onOpenEventDetail === 'function') {
                        onOpenEventDetail(event, image || null, group);
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Activity analysis
                    </Typography>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TIMELINE_CAMERA_LABEL_COLORS[0] }} />
                  </Stack>
                  {renderAnalysisDay(day)}
                </Box>
                {idx < daysWithEvents.length - 1 ? <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} /> : null}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const ActivityTimelineModal = ({
  open,
  onClose,
  group,
  events,
  loading,
  snapshotCache,
  thumbnail,
  onOpenEventDetail = null,
  onRequestAuditImage = null,
  /** Full event count for the selected range (matches card); not the current page length. */
  totalCount = 0,
  analysis = null,
  analysisLoading = false,
}) => {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down('sm'));
  const { openImagePreview } = useImagePreview();

  const groupedDays = useMemo(() => {
    const map = new Map();
    (events || []).forEach((event) => {
      const dt = parseActivityTimestamp(event?.event_time);
      const dateKey = !dt || Number.isNaN(dt.getTime()) ? String(event?.event_time || '') : format(dt, 'yyyy-MM-dd');
      const dateLabel = formatDayLabel(event?.event_time);
      if (!map.has(dateKey)) map.set(dateKey, { dateKey, dateLabel, events: [] });
      map.get(dateKey).events.push(event);
    });
    return Array.from(map.values())
      .map((day) => ({
        ...day,
        events: [...day.events].sort(compareEventTimesAsc),
      }))
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [events]);

  /** API returns analysis days separately; join by date so timeline and analysis display together. */
  const analysisDaysByDate = useMemo(() => {
    const map = new Map();
    (analysis?.days || []).forEach((day) => {
      const dateKey = String(day?.date || '').trim();
      if (dateKey) {
        map.set(dateKey, day);
      }
    });
    return map;
  }, [analysis?.days]);

  const combinedDays = useMemo(() => {
    const merged = new Map();
    groupedDays.forEach((day) => {
      merged.set(day.dateKey, { ...day, analysisDay: analysisDaysByDate.get(day.dateKey) });
    });
    analysisDaysByDate.forEach((day, dateKey) => {
      if (!merged.has(dateKey)) {
        merged.set(dateKey, { dateKey, dateLabel: formatDayLabel(day.date), events: [], analysisDay: day });
      }
    });
    return Array.from(merged.values()).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [groupedDays, analysisDaysByDate]);

  const allCameraLabels = useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => {
      const label = e?.camera_label || e?.camera_name || '';
      if (label) set.add(label);
    });
    (analysis?.days || []).forEach((day) => {
      (day?.breakdown || []).forEach((seg) => {
        const label = seg?.label || '';
        if (label) set.add(label);
      });
    });
    return Array.from(set).sort();
  }, [events, analysis?.days]);

  const colorForTimelineCameraDisplay = useMemo(
    () => buildTimelineCameraColorResolver(allCameraLabels),
    [allCameraLabels]
  );

  const timelineLegendLabels = useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => {
      const label = e?.camera_label || e?.camera_name || '';
      if (label) set.add(label);
    });
    return Array.from(set).sort();
  }, [events]);

  const totalDetections = (events || []).length;
  const violationCount = useMemo(
    () => (events || []).reduce((sum, ev) => sum + (ev?.is_violation ? 1 : 0), 0),
    [events]
  );
  const dayCount = combinedDays.length;
  const headingDateLabel = useMemo(() => {
    if (!combinedDays.length) return '-';
    if (combinedDays.length === 1) return combinedDays[0].dateLabel;
    const latest = combinedDays[0]?.dateLabel;
    const oldest = combinedDays[combinedDays.length - 1]?.dateLabel;
    return `${oldest} - ${latest}`;
  }, [combinedDays]);

  const hasAnyDayData = combinedDays.some((day) => day.events.length > 0 || day.analysisDay);
  const handleProfileImagePreview = (event) => {
    if (!thumbnail) return;
    event.preventDefault();
    event.stopPropagation();
    openImagePreview({
      src: thumbnail,
      title: group?.person_name || 'Profile image',
      alt: group?.person_name || 'Profile image',
      employeeName: group?.person_name || 'Unknown person',
      employeeId: group?.employee_code || group?.person_id || '',
      employeeImage: thumbnail,
      hideTitle: true,
    });
  };

  const renderAnalysisDay = (day) => {
    if (analysisLoading) {
      return (
        <Box sx={{ py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    if (!day?.analysisDay) {
      return (
        <Typography variant="body2" color="text.secondary">
          No breakdown for this day
        </Typography>
      );
    }

    const analysisDay = day.analysisDay;
    const breakdown = Array.isArray(analysisDay.breakdown) ? analysisDay.breakdown : [];
    const barSegments = buildVisibleAnalysisBarSegments(breakdown);

    return !breakdown.length ? (
      <Typography variant="body2" color="text.secondary">No events for this day</Typography>
    ) : (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
          {typeof analysisDay.total_events === 'number' ? `${analysisDay.total_events} events analyzed` : 'Activity breakdown'}
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1, rowGap: 1, mb: 1.25, alignItems: 'stretch' }}
        >
          {breakdown.map((seg) => {
            const percentLabel = formatAnalysisPercent(seg.percent);
            const labelText = String(seg.label || '').trim() || 'Other';
            const estimatedWidth = Math.min(360, Math.max(132, (labelText.length * 8) + 88));
            return (
              <Stack
                key={seg.label}
                direction="row"
                alignItems="center"
                spacing={0}
                sx={{
                  maxWidth: '100%',
                  minHeight: 34,
                  px: 1.2,
                  py: 0.65,
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.32)',
                  bgcolor: '#f8fafc',
                  boxSizing: 'border-box',
                  width: 'fit-content',
                  minWidth: { xs: '100%', sm: Math.min(220, estimatedWidth) },
                }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    columnGap: 0.5,
                    rowGap: 0.15,
                    width: '100%',
                    minWidth: 0,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.35,
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: '#334155',
                      fontWeight: 700,
                      maxWidth: '100%',
                    }}
                  >
                    {labelText}:
                  </Box>
                  <Box component="span" sx={{ color: '#475569', fontWeight: 600 }}>
                    {percentLabel}
                  </Box>
                  <Box component="span" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    ({seg.count})
                  </Box>
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <Box
          sx={{
            px: '2px',
            py: '3px',
            bgcolor: '#fff',
            borderRadius: 1.25,
            boxSizing: 'border-box',
            boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.35)',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              height: 8,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#f1f5f9',
            }}
          >
            {barSegments.map((seg, idx) => (
              <Box
                key={idx}
                sx={{
                  height: '100%',
                  width: `${seg.displayPercent}%`,
                  bgcolor: colorForTimelineCameraDisplay(seg.label),
                  transition: 'width 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobileModal}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      PaperProps={{
        sx: {
          borderRadius: isMobileModal ? 0 : 3,
          overflow: 'hidden',
          bgcolor: '#fafbfc',
          maxHeight: isMobileModal ? '100vh' : '85vh',
        },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={thumbnail || undefined}
              alt={group?.person_name || 'Unknown person'}
              imgProps={{
                'data-no-image-preview': 'true',
              }}
              onClick={handleProfileImagePreview}
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                bgcolor: '#40518A',
                fontWeight: 600,
                fontSize: { xs: '1.1rem', sm: '1.5rem' },
                flexShrink: 0,
                cursor: thumbnail ? 'pointer' : 'default',
              }}
            >
              {(group?.person_name || group?.person_id || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <EmployeeNameHyperlink
                    text={group?.person_name || 'Unknown Person'}
                    employees={[buildPersonLinkMeta(group, { routeSource: 'event' })]}
                    style={{ display: 'inline' }}
                  />
                </Typography>
                {groupShowsVendorBadge(group, events) ? (
                  <Tooltip title="Vendor" arrow placement="bottom">
                    <AssignmentIndIcon sx={{ fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: '#40518A', flexShrink: 0 }} />
                  </Tooltip>
                ) : null}
                {groupShowsVisitorBadge(group, events) ? (
                  <Tooltip title="Visitor" arrow placement="bottom">
                    <BadgeIcon sx={{ fontSize: { xs: '1.15rem', sm: '1.35rem' }, color: '#40518A', flexShrink: 0 }} />
                  </Tooltip>
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {group?.employee_code || group?.person_id || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                Date: {headingDateLabel}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Detections: ${totalDetections}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
                {violationCount > 0 ? (
                  <Chip size="small" label={`Violations: ${violationCount}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                ) : null}
                <Chip size="small" label={`Days: ${dayCount}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#fef9c3', color: '#854d0e' }} />
                <Chip size="small" label={`Last Seen: ${group?.last_seen ? formatShortTime(group.last_seen) : '-'}`} sx={{ height: { xs: 20, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fafbfc', overflowY: 'auto', maxHeight: isMobileModal ? 'calc(100vh - 180px)' : 'calc(85vh - 200px)' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, sm: 0 }}
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TIMELINE_CAMERA_LABEL_COLORS[0] }} />
          </Stack>
          <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
            {timelineLegendLabels.map((label) => (
              <Stack key={label} direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: { xs: 6, sm: 8 },
                    height: { xs: 6, sm: 8 },
                    bgcolor: colorForTimelineCameraDisplay(label),
                    borderRadius: '50%',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}
                >
                  {label}
                </Typography>
              </Stack>
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 22, sm: 28 }, height: { xs: 10, sm: 14 }, bgcolor: '#e5e7eb', borderRadius: 999 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Timeline</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: '#cbd5e1', border: '2px solid #fff' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Image Marker</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: { xs: 12, sm: 16 }, height: { xs: 12, sm: 16 }, borderRadius: '50%', bgcolor: '#94a3b8', border: '2px solid #fff', position: 'relative' }}>
                <Box sx={{ position: 'absolute', width: { xs: 4, sm: 6 }, height: { xs: 4, sm: 6 }, borderRadius: '50%', bgcolor: '#dc2626', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } , fontWeight: 600 }}>Violation</Typography>
            </Stack>
          </Stack>
        </Stack>
        {hasAnyDayData ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
            Hover a timeline dot to load its recognition snapshot; click for full details.
          </Typography>
        ) : null}

        {loading ? (
          <Stack spacing={2} sx={{ py: 2 }}>
            {[1, 2].map((i) => (
              <Box key={`timeline-skel-${i}`} sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
                <Box sx={{ width: '45%', height: 16, borderRadius: 1, mb: 1.5, ...shimmerStyles }} />
                <Box sx={{ height: 44, borderRadius: 2, mb: 2, ...shimmerStyles }} />
                <Box sx={{ width: '30%', height: 12, borderRadius: 1, mb: 1, ...shimmerStyles }} />
                <Stack spacing={1}>
                  <Box sx={{ height: 12, borderRadius: 1, ...shimmerStyles }} />
                  <Box sx={{ height: 12, borderRadius: 1, width: '86%', ...shimmerStyles }} />
                  <Box sx={{ height: 12, borderRadius: 1, width: '72%', ...shimmerStyles }} />
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : !hasAnyDayData ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">No activity recorded for this period</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2.5 }}>
            {combinedDays.map((day, idx) => (
              <Box key={day.dateKey}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#1f2937', mb: 1.25, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                >
                  {day.dateLabel}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {day.events.length ? (
                    <ActivityTimelineBar
                      events={day.events}
                      snapshotCache={snapshotCache}
                      colorForCameraLabel={colorForTimelineCameraDisplay}
                      onRequestAuditImage={onRequestAuditImage}
                      onMarkerClick={(event, image) => {
                        if (typeof onOpenEventDetail === 'function') {
                          onOpenEventDetail(event, image || null, group);
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">No timeline events for this day</Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Activity analysis
                    </Typography>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TIMELINE_CAMERA_LABEL_COLORS[0] }} />
                  </Stack>
                  {renderAnalysisDay(day)}
                </Box>
                {idx < combinedDays.length - 1 ? <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} /> : null}
              </Box>
            ))}
          </Box>
        )}
      </Box>

    </Dialog>
  );
};

const ActivityLogs = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const initialSearchParams = new URLSearchParams(location.search);
  const urlStartDate = parseDateParam(initialSearchParams.get('start_date')) || new Date();
  const urlEndDate = parseDateParam(initialSearchParams.get('end_date')) || urlStartDate;
  const urlStartTime = normalizeTimeParam(initialSearchParams.get('start_time'), '00:00');
  const urlEndTime = normalizeTimeParam(initialSearchParams.get('end_time'), '23:59');
  const [groups, setGroups] = useState([]);
  const [cameraLabelGroups, setCameraLabelGroups] = useState([]);
  const [pagination, setPagination] = useState({ limit: PAGE_LIMIT, offset: 0, total: 0, current_page: 1 });
  const [loading, setLoading] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [search, setSearch] = useState('');
  const [availableNames, setAvailableNames] = useState([]);
  const [dateRange, setDateRange] = useState([
    { startDate: urlStartDate, endDate: urlEndDate, key: 'selection' },
  ]);
  const [draftDateRange, setDraftDateRange] = useState([
    { startDate: urlStartDate, endDate: urlEndDate, key: 'selection' },
  ]);
  const [startTime, setStartTime] = useState(urlStartTime);
  const [endTime, setEndTime] = useState(urlEndTime);
  const [draftStartTime, setDraftStartTime] = useState(urlStartTime);
  const [draftEndTime, setDraftEndTime] = useState(urlEndTime);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [personThumbnails, setPersonThumbnails] = useState({});
  const [timelineDialog, setTimelineDialog] = useState({
    open: false,
    group: null,
    events: [],
    loading: false,
    page: 1,
    total: 0,
    analysis: null,
    analysisLoading: false,
  });
  const [processingDialog, setProcessingDialog] = useState({ open: false, log: null, stack: [] });
  const [activityCameras, setActivityCameras] = useState([]);
  const [cameraId, setCameraId] = useState('');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState(EMPLOYEE_TYPE_FILTER_DEFAULT);
  const [violationFilter, setViolationFilter] = useState(
    (() => {
      const raw = String(new URLSearchParams(location.search).get('violation') || 'all').toLowerCase();
      return raw === 'violation' ? 'violation' : 'all';
    })()
  );
  const [activityExportDialogOpen, setActivityExportDialogOpen] = useState(false);
  const [activityExportLoading, setActivityExportLoading] = useState(false);
  const [activityExportFields, setActivityExportFields] = useState([]);
  const [activityExportSelectedFields, setActivityExportSelectedFields] = useState([]);
  const [activityExportFileName, setActivityExportFileName] = useState('activity_logs');
  const [activityExportDateRange, setActivityExportDateRange] = useState(defaultDateRange());
  const [activityExportStartTime, setActivityExportStartTime] = useState('00:00');
  const [activityExportEndTime, setActivityExportEndTime] = useState('23:59');
  const [activityExportDatePickerAnchor, setActivityExportDatePickerAnchor] = useState(null);
  const [activityExportPersonName, setActivityExportPersonName] = useState('');
  const [activityExportCameraId, setActivityExportCameraId] = useState('');
  const [activityExportFormat, setActivityExportFormat] = useState('csv');
  const [activityExportMenuAnchor, setActivityExportMenuAnchor] = useState(null);
  const { confirm } = useConfirmDialog();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const personThumbnailsRef = useRef({});
  const auditImageLoadPromisesRef = useRef(new Map());
  const personThumbnailLoadPromisesRef = useRef(new Map());

  const datePickerOpen = Boolean(datePickerAnchor);
  const activityExportDatePickerOpen = Boolean(activityExportDatePickerAnchor);
  const activityExportMenuOpen = Boolean(activityExportMenuAnchor);
  const selectedStart = dateRange[0]?.startDate || new Date();
  const selectedEnd = dateRange[0]?.endDate || selectedStart;
  const formattedStart = buildDateTimeString(selectedStart, startTime, '00:00');
  const formattedEnd = buildDateTimeString(selectedEnd, endTime, '23:59');

  const activityGridSx = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(1, minmax(0, 1fr))',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(3, minmax(0, 1fr))',
      xl: 'repeat(3, minmax(0, 1fr))',
    },
    gap: 2.5,
    alignItems: 'stretch',
  }), []);

  const getTimingDialogResultMeta = useCallback((log) => {
    if (!log) return { label: 'Unknown', color: 'default' };
    if (log.confidence_level === 'possible_match') return { label: 'Possible Match', color: 'warning' };
    if (log.success) return { label: 'Matched', color: 'success' };
    if (log.confidence_level === 'no_match') return { label: 'Unmatched', color: 'error' };
    return { label: 'Failed', color: 'error' };
  }, []);

  const mapActivityEventToTimingLog = useCallback((event, detail = null, fallbackGroup = null) => {
    const src = detail || event || {};
    if (event?.event_kind === 'attendance' || src?.event_kind === 'attendance') {
      const eventTime = src.event_time || event?.event_time || null;
      const eventTimeMs = eventTime ? Date.parse(eventTime) : null;
      const action = (src.attendance_action || event?.attendance_action || '').replace(/_/g, ' ');
      return {
        audit_id: src.audit_id || event?.audit_id || null,
        operation_type: 'attendance',
        recognized_name: src.person_name || event?.person_name || fallbackGroup?.person_name || 'Unknown person',
        recognized_person_id: src.person_id || event?.person_id || fallbackGroup?.person_id || null,
        employee_code: src.employee_code || event?.employee_code || fallbackGroup?.employee_code || null,
        employee_type:
          src.employee_type ?? event?.employee_type ?? fallbackGroup?.employee_type ?? null,
        track_id: null,
        camera: src.camera_label || event?.camera_label || 'Attendance',
        confidence_score: null,
        confidence_level: action || 'attendance',
        success: true,
        created_at: eventTime,
        captured_at: eventTime,
        captured_at_ms: Number.isFinite(eventTimeMs) ? eventTimeMs : null,
        request_metadata: {
          attendance_action: src.attendance_action || event?.attendance_action || null,
          activity_id: src.activity_id || event?.activity_id || null,
        },
      };
    }
    const eventTime = src.event_time || event?.event_time || null;
    const eventTimeMs = eventTime ? Date.parse(eventTime) : null;
    const recognizedPersonId = src.person_id || event?.person_id || fallbackGroup?.person_id || null;
    const confidenceLevel = src.confidence_level ?? event?.confidence_level ?? null;
    const hasKnownConfidence = confidenceLevel != null && String(confidenceLevel).trim() !== '';
    const hasMatch = hasKnownConfidence
      && Boolean(recognizedPersonId)
      && confidenceLevel !== 'no_match'
      && confidenceLevel !== 'possible_match'
      && confidenceLevel !== 'error'
      && confidenceLevel !== 'no_face';
    const requestMetadata = {
      camera_id: src.camera_id || event?.camera_id || null,
      camera_label: src.camera_label || event?.camera_label || null,
      camera_type: src.camera_type || event?.camera_type || null,
      activity_id: src.activity_id || event?.activity_id || null,
      timing_total_ms: src.timing_total_ms ?? null,
    };
    if (src.detection_confidence != null) {
      requestMetadata.detection_confidence = src.detection_confidence;
    }
    return {
      audit_id: src.audit_id || event?.audit_id || src.activity_id || event?.activity_id || null,
      operation_type: 'activity',
      recognized_name: src.person_name || event?.person_name || fallbackGroup?.person_name || 'Unknown person',
      recognized_person_id: recognizedPersonId,
      employee_code: src.employee_code || fallbackGroup?.employee_code || null,
      employee_type:
        src.employee_type ?? event?.employee_type ?? fallbackGroup?.employee_type ?? null,
      track_id: src.track_id || event?.track_id || null,
      camera: src.camera_label || event?.camera_label || src.camera_type || event?.camera_type || 'ACTIVITY',
      confidence_score: src.confidence_score ?? event?.confidence_score ?? null,
      confidence_level: confidenceLevel,
      success: hasMatch,
      created_at: eventTime,
      captured_at: eventTime,
      captured_at_ms: Number.isFinite(eventTimeMs) ? eventTimeMs : null,
      timing_capture_to_worker_ms: src.timing_capture_to_worker_ms ?? null,
      timing_frame_prep_ms: src.timing_frame_prep_ms ?? null,
      timing_detection_ms: src.timing_detection_ms ?? null,
      timing_queue_prep_ms: src.timing_queue_prep_ms ?? null,
      timing_queue_wait_ms: src.timing_queue_wait_ms ?? null,
      processing_time_ms: src.processing_time_ms ?? null,
      faiss_search_time_ms: src.faiss_search_time_ms ?? null,
      request_metadata: requestMetadata,
    };
  }, []);

  const closeProcessingDialog = useCallback(() => {
    setProcessingDialog({ open: false, log: null, stack: [] });
  }, []);

  const loadAuditImageIfNeeded = useCallback((auditId) => {
    if (!auditId) {
      return Promise.resolve(null);
    }
    const cached = peekAuditImage(auditId);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
    const inFlight = auditImageLoadPromisesRef.current.get(auditId);
    if (inFlight) {
      return inFlight;
    }
    const p = fetchAuditImage(auditId).finally(() => {
      auditImageLoadPromisesRef.current.delete(auditId);
    });
    auditImageLoadPromisesRef.current.set(auditId, p);
    return p;
  }, []);

  const loadPersonThumbnailIfNeeded = useCallback((personId) => {
    if (!personId) {
      return Promise.resolve(null);
    }
    if (Object.prototype.hasOwnProperty.call(personThumbnailsRef.current, personId)) {
      return Promise.resolve(personThumbnailsRef.current[personId]);
    }
    const inFlight = personThumbnailLoadPromisesRef.current.get(personId);
    if (inFlight) {
      return inFlight;
    }
    const p = (async () => {
      try {
        const faces = await getPersonFaces(personId);
        const faceList = Array.isArray(faces) ? faces : [];
        const preferredFace = faceList.find((face) => !face.deleted_at) || faceList[0];
        if (!preferredFace?.face_id) {
          setPersonThumbnails((prev) => {
            if (prev[personId] !== undefined) return prev;
            const next = { ...prev, [personId]: null };
            personThumbnailsRef.current = next;
            return next;
          });
          return null;
        }
        const blob = await getFaceImage(preferredFace.face_id);
        const url = URL.createObjectURL(blob);
        setPersonThumbnails((prev) => {
          if (prev[personId] !== undefined) {
            if (url) URL.revokeObjectURL(url);
            return prev;
          }
          const next = { ...prev, [personId]: url };
          personThumbnailsRef.current = next;
          return next;
        });
        return url;
      } catch {
        setPersonThumbnails((prev) => {
          if (prev[personId] !== undefined) return prev;
          const next = { ...prev, [personId]: null };
          personThumbnailsRef.current = next;
          return next;
        });
        return null;
      } finally {
        personThumbnailLoadPromisesRef.current.delete(personId);
      }
    })();
    personThumbnailLoadPromisesRef.current.set(personId, p);
    return p;
  }, []);

  const loadGroupPreviewImagesIfNeeded = useCallback((group) => {
    if (!group) return;
    if (group.person_id) {
      loadPersonThumbnailIfNeeded(group.person_id);
    }
    if (group.latest_audit_id) {
      loadAuditImageIfNeeded(group.latest_audit_id);
    }
    const previewEvents = Array.isArray(group.recent_events) ? group.recent_events.slice(0, 3) : [];
    previewEvents.forEach((event) => {
      if (event?.audit_id) {
        loadAuditImageIfNeeded(event.audit_id);
      }
    });
  }, [loadAuditImageIfNeeded, loadPersonThumbnailIfNeeded]);

  const openProcessingDialog = useCallback(async (event, image = null, group = null) => {
    if (!event) return;
    const activityId = event?.activity_id;
    const isAttendance = event?.event_kind === 'attendance';
    const needsDetail = Boolean(activityId) && !isAttendance && (event?.confidence_level == null || event?.confidence_level === '');

    const imagePromise = image != null
      ? Promise.resolve(image)
      : (event?.audit_id ? loadAuditImageIfNeeded(event.audit_id) : Promise.resolve(null));
    const detailPromise = needsDetail
      ? getActivityLogDetail(activityId).catch(() => null)
      : Promise.resolve(null);

    const [stackImage, initialDetail] = await Promise.all([imagePromise, detailPromise]);
    const baseLog = mapActivityEventToTimingLog(event, initialDetail, group);
    setProcessingDialog({
      open: true,
      log: baseLog,
      stack: [{ log: baseLog, image: stackImage }],
    });

    if (!activityId || isAttendance || initialDetail) return;

    try {
      const detail = await getActivityLogDetail(activityId);
      setProcessingDialog((prev) => {
        if (!prev.open || String(prev.log?.audit_id || '') !== String(baseLog.audit_id || '')) {
          return prev;
        }
        const mergedLog = mapActivityEventToTimingLog(event, detail, group);
        return {
          ...prev,
          log: mergedLog,
          stack: [{ log: mergedLog, image: stackImage }],
        };
      });
    } catch (_) {
      // Keep base payload if detail endpoint fails.
    }
  }, [loadAuditImageIfNeeded, mapActivityEventToTimingLog]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasStart = Boolean(params.get('start_date'));
    const hasEnd = Boolean(params.get('end_date'));
    if (hasStart && hasEnd) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    if (!hasStart) params.set('start_date', today);
    if (!hasEnd) params.set('end_date', today);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextStart = parseDateParam(params.get('start_date')) || new Date();
    const nextEnd = parseDateParam(params.get('end_date')) || nextStart;
    const nextStartTime = normalizeTimeParam(params.get('start_time'), '00:00');
    const nextEndTime = normalizeTimeParam(params.get('end_time'), '23:59');
    setDateRange((prev) => {
      const prevStart = prev[0]?.startDate;
      const prevEnd = prev[0]?.endDate;
      if (
        prevStart?.toDateString() === nextStart.toDateString() &&
        prevEnd?.toDateString() === nextEnd.toDateString()
      ) {
        return prev;
      }
      return [{ startDate: nextStart, endDate: nextEnd, key: 'selection' }];
    });
    setStartTime((prev) => (prev === nextStartTime ? prev : nextStartTime));
    setEndTime((prev) => (prev === nextEndTime ? prev : nextEndTime));
    setDraftDateRange((prev) => {
      const prevStart = prev[0]?.startDate;
      const prevEnd = prev[0]?.endDate;
      if (
        prevStart?.toDateString() === nextStart.toDateString() &&
        prevEnd?.toDateString() === nextEnd.toDateString()
      ) {
        return prev;
      }
      return [{ startDate: nextStart, endDate: nextEnd, key: 'selection' }];
    });
    setDraftStartTime((prev) => (prev === nextStartTime ? prev : nextStartTime));
    setDraftEndTime((prev) => (prev === nextEndTime ? prev : nextEndTime));
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = String(params.get('violation') || 'all').toLowerCase();
    const next = raw === 'violation' ? 'violation' : 'all';
    setViolationFilter((prev) => (prev === next ? prev : next));
  }, [location.search]);

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit));
  }, [pagination.total, pagination.limit]);
  const filteredGroups = useMemo(() => {
    return groups;
  }, [groups]);
  const colorForFilterCamera = useMemo(
    () => buildTimelineCameraColorResolver((cameraLabelGroups || []).map((r) => r?.label).filter(Boolean)),
    [cameraLabelGroups],
  );
  const safeAvailableNames = Array.isArray(availableNames) ? availableNames : [];
  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const activityExportVisibleFields = useMemo(
    () => activityExportFields.filter((field) => field.enabled !== false),
    [activityExportFields]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if(cancelled) return undefined;

        const res = await getAllPersonNames();
        const names = res?.map((d)=>`${d.name} (${d.person_id})`) || [];
        setAvailableNames(res || []);
        
      } catch (_) {
        if (!cancelled) setAvailableNames([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadGroups = useCallback(async (limit, offset) => {
    setLoading(true);
    try {
      const roleParams = employeeTypeFilterToRoleParams(employeeTypeFilter);
      const response = await getActivityLogGroups({
        start_time: formattedStart,
        end_time: formattedEnd,
        person_name: search,
        camera_id: cameraId || undefined,
        is_unknown: false,
        is_violation: violationFilter === 'violation' ? true : undefined,
        ...roleParams,
        limit,
        offset,
      });
      const nextGroups = Array.isArray(response?.groups) ? response.groups : [];
      const nextPagination = response?.pagination || {};
      setGroups(nextGroups);
      setCameraLabelGroups(
        Array.isArray(response?.camera_label_groups) ? response.camera_label_groups : [],
      );
      setPagination({
        limit: nextPagination.limit || limit,
        offset: nextPagination.offset || offset,
        total: nextPagination.total || 0,
        current_page: nextPagination.current_page || Math.floor(offset / limit) + 1,
      });
    } finally {
      setLoading(false);
    }
  }, [formattedStart, formattedEnd, search, cameraId, employeeTypeFilter, violationFilter]);

  useEffect(() => {
    loadGroups(PAGE_LIMIT, 0);
  }, [loadGroups]);

  useEffect(() => {
    personThumbnailsRef.current = personThumbnails;
  }, [personThumbnails]);

  useEffect(() => {
    return () => {
      Object.values(personThumbnailsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const loadActivityCameras = useCallback(async () => {
    try {
      const response = await getActivityCameras();
      const cameras = Array.isArray(response?.cameras) ? response.cameras : [];
      setActivityCameras(cameras);
      return cameras;
    } catch (_) {
      setActivityCameras([]);
      return [];
    }
  }, []);

  useEffect(() => {
    loadActivityCameras();
  }, [loadActivityCameras]);

  const TIMELINE_FETCH_CAP = 5000;

  const openTimeline = async (group) => {
    const cardTotal = Number(group?.record_count);
    const limit =
      Number.isFinite(cardTotal) && cardTotal > 0
        ? Math.min(TIMELINE_FETCH_CAP, cardTotal)
        : TIMELINE_FETCH_CAP;
    setTimelineDialog({
      open: true,
      group,
      events: [],
      loading: true,
      page: 1,
      total: 0,
      analysis: null,
      analysisLoading: true,
    });
    try {
      const [timelineResult, analysisResult] = await Promise.allSettled([
        getActivityGroupTimeline(group.group_key, {
          limit,
          offset: 0,
          start_time: formattedStart,
          end_time: formattedEnd,
          camera_id: cameraId || undefined,
        }),
        getActivityGroupCameraAnalysis(group.group_key, {
          start_time: formattedStart,
          end_time: formattedEnd,
          camera_id: cameraId || undefined,
        }),
      ]);
      const response = timelineResult.status === 'fulfilled' ? timelineResult.value : null;
      const events = Array.isArray(response?.events) ? response.events : [];
      const analysisPayload =
        analysisResult.status === 'fulfilled' && analysisResult.value && Array.isArray(analysisResult.value.days)
          ? analysisResult.value
          : null;
      setTimelineDialog({
        open: true,
        group,
        events,
        loading: false,
        page: 1,
        total: response?.pagination?.total || 0,
        analysis: analysisPayload,
        analysisLoading: false,
      });
    } catch (_) {
      setTimelineDialog({
        open: true,
        group,
        events: [],
        loading: false,
        page: 1,
        total: 0,
        analysis: null,
        analysisLoading: false,
      });
    }
  };

  const openDatePicker = (event) => {
    const current = dateRange[0] || { startDate: new Date(), endDate: new Date(), key: 'selection' };
    setDraftDateRange([{ startDate: current.startDate, endDate: current.endDate, key: 'selection' }]);
    setDraftStartTime(startTime);
    setDraftEndTime(endTime);
    setDatePickerAnchor(event.currentTarget);
  };

  const applyDateRange = () => {
    const current = draftDateRange[0] || { startDate: new Date(), endDate: new Date(), key: 'selection' };
    const nextStartTime = normalizeTimeParam(draftStartTime, '00:00');
    const nextEndTime = normalizeTimeParam(draftEndTime, '23:59');
    const params = new URLSearchParams(location.search);
    params.set('start_date', format(current.startDate, 'yyyy-MM-dd'));
    params.set('end_date', format(current.endDate, 'yyyy-MM-dd'));
    if (nextStartTime !== '00:00') params.set('start_time', nextStartTime); else params.delete('start_time');
    if (nextEndTime !== '23:59') params.set('end_time', nextEndTime); else params.delete('end_time');
    setDateRange([{ startDate: current.startDate, endDate: current.endDate, key: 'selection' }]);
    setStartTime(nextStartTime);
    setEndTime(nextEndTime);
    navigate({ pathname: location.pathname, search: params.toString() });
    setDatePickerAnchor(null);
  };

  const applyNameSearch = (value) => {
    const sanitized = sanitizeNameSearchInput(value || '');
    setNameInput(sanitized);
    setSearch(sanitized);
  };

  const hasActiveFilters = useMemo(() => (
    !!(search?.trim()) ||
    !!cameraId ||
    employeeTypeFilter !== EMPLOYEE_TYPE_FILTER_DEFAULT ||
    violationFilter !== 'all' ||
    !isDefaultDateTimeRange(dateRange, startTime, endTime)
  ), [cameraId, dateRange, employeeTypeFilter, endTime, search, startTime, violationFilter]);

  const handleClearAllFilters = useCallback(() => {
    const todayRange = createTodayDateRange();
    setSearch('');
    setNameInput('');
    setCameraId('');
    setEmployeeTypeFilter(EMPLOYEE_TYPE_FILTER_DEFAULT);
    setViolationFilter('all');
    setDateRange(todayRange);
    setDraftDateRange(todayRange);
    setStartTime(DEFAULT_START_TIME);
    setEndTime(DEFAULT_END_TIME);
    setDraftStartTime(DEFAULT_START_TIME);
    setDraftEndTime(DEFAULT_END_TIME);
    const params = new URLSearchParams(location.search);
    params.set('start_date', format(todayRange[0].startDate, 'yyyy-MM-dd'));
    params.set('end_date', format(todayRange[0].endDate, 'yyyy-MM-dd'));
    params.delete('start_time');
    params.delete('end_time');
    params.delete('violation');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const openActivityExportMenu = (event) => {
    setActivityExportMenuAnchor(event.currentTarget);
  };

  const closeActivityExportMenu = () => {
    setActivityExportMenuAnchor(null);
  };

  const handleChooseExportFormat = (formatType) => {
    closeActivityExportMenu();
    handleOpenActivityExportDialog(formatType);
  };

  const moveSelectedExportField = useCallback((fromIndex, direction) => {
    setActivityExportSelectedFields((prev) => {
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const toggleActivityExportField = useCallback((fieldName, checked) => {
    setActivityExportSelectedFields((prev) => {
      if (checked) return prev.includes(fieldName) ? prev : [...prev, fieldName];
      return prev.filter((item) => item !== fieldName);
    });
  }, []);

  const buildExportDateTimeRange = useCallback(() => {
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const selection = activityExportDateRange[0] || { startDate: new Date(), endDate: new Date() };
    const startDate = selection.startDate || new Date();
    const endDate = selection.endDate || startDate;
    const [startHour, startMinute] = parseTime(activityExportStartTime, 0, 0);
    const [endHour, endMinute] = parseTime(activityExportEndTime, 23, 59);

    const localStart = new Date(startDate);
    localStart.setHours(startHour, startMinute, 0, 0);
    const localEnd = new Date(endDate);
    localEnd.setHours(endHour, endMinute, 59, 0);
    if (localStart > localEnd) return { error: 'Start date/time cannot be after end date/time' };

    const startDateTimeUTC = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0
    ));
    const endDateTimeUTC = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      59
    ));
    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  }, [activityExportDateRange, activityExportEndTime, activityExportStartTime]);

  const handleOpenActivityExportDialog = useCallback((formatType = 'csv') => {
    const startDate = dateRange[0]?.startDate || new Date();
    const endDate = dateRange[0]?.endDate || startDate;
    const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
    const cameraLabels = Array.isArray(cameraLabelGroups)
      ? cameraLabelGroups.map((row) => row?.label).filter(Boolean)
      : [];
    const fields = [...ACTIVITY_EXPORT_FIELDS, ...buildCameraCountFieldDefs(cameraLabels)];
    setActivityExportFormat(normalizedFormat);
    setActivityExportFields(fields);
    setActivityExportSelectedFields(fields.filter((field) => field.enabled !== false).map((field) => field.field));
    setActivityExportPersonName(search || '');
    setActivityExportCameraId(cameraId || '');
    setActivityExportFileName('activity_logs');
    setActivityExportDateRange([{ startDate, endDate, key: 'selection' }]);
    setActivityExportStartTime('00:00');
    setActivityExportEndTime('23:59');
    setActivityExportDatePickerAnchor(null);
    setActivityExportDialogOpen(true);
  }, [cameraId, cameraLabelGroups, dateRange, search]);

  const handleExportActivity = useCallback(async () => {
    try {
      setActivityExportLoading(true);
      const visibleFields = activityExportFields.filter((field) => field.enabled !== false);
      const fieldByName = new Map(visibleFields.map((field) => [field.field, field]));
      const selectedFields = activityExportSelectedFields.filter((field) => fieldByName.has(field));
      if (!selectedFields.length) {
        show('Select at least one field to export', 'error');
        return;
      }

      const range = buildExportDateTimeRange();
      if (range?.error) {
        show(range.error, 'error');
        return;
      }

      const selectedFieldDefs = selectedFields
        .map((field) => fieldByName.get(field))
        .filter(Boolean);
      const selectedCameraCountFields = selectedFieldDefs.filter(
        (field) => field?.kind === 'cameraCount' || String(field?.field || '').startsWith(CAMERA_COUNT_FIELD_PREFIX),
      );
      const exportFields = selectedFieldDefs.map((field) => field.field);
      const safeFileName = (activityExportFileName || 'activity_logs').trim() || 'activity_logs';
      const roleParams = employeeTypeFilterToRoleParams(employeeTypeFilter);
      const { blob, filename } = await exportActivityLogs({
        start_time: range.formattedStart,
        end_time: range.formattedEnd,
        person_name: (activityExportPersonName || '').trim(),
        camera_id: activityExportCameraId || undefined,
        is_unknown: false,
        ...roleParams,
        selected_fields: exportFields,
        export_format: activityExportFormat,
        file_name: safeFileName,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fallbackExt = activityExportFormat === 'xlsx' ? 'xlsx' : 'csv';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = filename || `${safeFileName}_${timestamp}.${fallbackExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setActivityExportDialogOpen(false);
      show(`Activity logs exported successfully as ${activityExportFormat.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Failed to export activity logs:', error);
      show(error?.message || 'Failed to export activity logs', 'error');
    } finally {
      setActivityExportLoading(false);
    }
  }, [
    activityExportCameraId,
    activityExportFields,
    activityExportFileName,
    activityExportFormat,
    activityExportPersonName,
    activityExportSelectedFields,
    buildExportDateTimeRange,
    employeeTypeFilter,
    show,
  ]);

  const handleExportActivityWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Export',
      message: `Are you sure you want to export activity logs as ${activityExportFormat.toUpperCase()}?`,
      confirmLabel: `Export ${activityExportFormat.toUpperCase()}`,
      danger: false,
    });
    if (!approved) return;
    await handleExportActivity();
  }, [confirm, activityExportFormat, handleExportActivity]);

  const rebuildActivityExportFields = useCallback((cameraLabels = []) => {
    setActivityExportFields((prevFields) => {
      const prevFieldNames = new Set((Array.isArray(prevFields) ? prevFields : []).map((field) => field.field));
      const nextFields = [...ACTIVITY_EXPORT_FIELDS, ...buildCameraCountFieldDefs(cameraLabels)];
      const nextFieldNames = new Set(nextFields.map((field) => field.field));
      const newlyAddedEnabledFields = nextFields
        .filter((field) => field.enabled !== false && !prevFieldNames.has(field.field))
        .map((field) => field.field);

      setActivityExportSelectedFields((prevSelected) => {
        const kept = (Array.isArray(prevSelected) ? prevSelected : []).filter((field) => nextFieldNames.has(field));
        const nextSelected = [...kept];
        newlyAddedEnabledFields.forEach((field) => {
          if (!nextSelected.includes(field)) nextSelected.push(field);
        });
        return nextSelected;
      });

      return nextFields;
    });
  }, []);

  useEffect(() => {
    if (!activityExportDialogOpen) return () => {};
    let cancelled = false;
    const timer = setTimeout(async () => {
      const range = buildExportDateTimeRange();
      if (cancelled || range?.error) return;
      try {
        const roleParams = employeeTypeFilterToRoleParams(employeeTypeFilter);
        const res = await getActivityLogGroups({
          start_time: range.formattedStart,
          end_time: range.formattedEnd,
          person_name: (activityExportPersonName || '').trim(),
          camera_id: activityExportCameraId || undefined,
          is_unknown: false,
          ...roleParams,
          limit: 1,
          offset: 0,
        });
        const labels = Array.isArray(res?.camera_label_groups)
          ? res.camera_label_groups.map((row) => row?.label).filter(Boolean)
          : [];
        if (!cancelled) rebuildActivityExportFields(labels);
      } catch (_) {
        if (!cancelled) rebuildActivityExportFields([]);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activityExportCameraId,
    activityExportDateRange,
    activityExportDialogOpen,
    activityExportEndTime,
    activityExportPersonName,
    activityExportStartTime,
    employeeTypeFilter,
    buildExportDateTimeRange,
    rebuildActivityExportFields,
  ]);

  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);
  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      {!embedded ? (
        <PageHeader
          title="Activity"
          subtitle="Person grouped activity monitoring timeline"
          icon={null}
        />
      ) : null}

      <Box sx={pageFiltersToolbarSx}>
        <Stack {...pageFiltersStackSx}>
          <Box sx={pageFiltersControlsSx}>
            <Autocomplete
              size="small"
              freeSolo
              options={safeAvailableNames}
              getOptionLabel={formatEnrolledPersonNameOption}
              filterOptions={filterEnrolledPersonNameOptions}
              value={search || null}
              inputValue={nameInput}
              onInputChange={(event, newValue) => {
                setNameInput(sanitizeNameSearchInput(newValue || ''));
              }}
              onChange={(e, newValue) => {
                    const selectedValue =
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.name || '';

                    applyNameSearch(selectedValue);
                  }} 
                renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by name or person id"
                  onKeyDown={(event) => {
                    if (typeof params?.inputProps?.onKeyDown === 'function') {
                      params.inputProps.onKeyDown(event);
                    }
                    if (event.key === 'Enter') {
                      applyNameSearch(event.currentTarget.value || '');
                    }
                  }}
                />
              )}
              sx={pageFilterAutocompleteSx(240)}
            />
            <FormControl size="small" sx={pageFilterFormControlSx(200)}>
              <InputLabel id="attendance-activity-camera-label">Camera</InputLabel>
              <Select
                labelId="attendance-activity-camera-label"
                label="Camera"
                value={cameraId}
                onChange={(event) => setCameraId(event.target.value)}
              >
                <MenuItem value="">All Cameras</MenuItem>
                {activityCameras.map((camera) => (
                  <MenuItem key={camera.camera_id || camera.label} value={camera.camera_id || ''}>
                    {camera.label || camera.camera_id || 'Activity camera'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={pageFilterFormControlSx(170)}>
              <InputLabel id="activity-employee-type-label">Activity Type</InputLabel>
              <Select
                labelId="activity-employee-type-label"
                label="Activity Type"
                value={employeeTypeFilter}
                onChange={(event) => setEmployeeTypeFilter(event.target.value)}
              >
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="visitor">Visitor</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={pageFilterFormControlSx(170)}>
              <InputLabel id="activity-violation-filter-label">Violation</InputLabel>
              <Select
                labelId="activity-violation-filter-label"
                label="Violation"
                value={violationFilter}
                onChange={(event) => {
                  const next = event.target.value === 'violation' ? 'violation' : 'all';
                  setViolationFilter(next);
                  const params = new URLSearchParams(location.search);
                  if (next === 'violation') params.set('violation', 'violation');
                  else params.delete('violation');
                  navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="violation">Violation</MenuItem>
              </Select>
            </FormControl>
            <Box
              onClick={openDatePicker}
              sx={pageFilterDatePickerSx(220)}
            >
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {isSameDay(selectedStart, selectedEnd)
                  ? format(selectedStart, 'MMM dd, yyyy')
                  : `${format(selectedStart, 'MMM dd')} - ${format(selectedEnd, 'MMM dd, yyyy')}`}
              </Typography>
            </Box>
            <Popover
              open={datePickerOpen}
              anchorEl={datePickerAnchor}
              onClose={() => setDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', overflow: 'auto' },
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRight: { md: '1px solid' },
                    borderBottom: { xs: '1px solid', md: 'none' },
                    borderColor: 'divider',
                    minWidth: 140,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                      { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                      { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                      { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const value = preset.getValue();
                          setDraftDateRange([{ startDate: value.startDate, endDate: value.endDate, key: 'selection' }]);
                        }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
                <Box sx={{ p: 2 }}>
                  <DateRangePicker
                    ranges={draftDateRange}
                    onChange={(ranges) => setDraftDateRange([ranges.selection])}
                    direction="horizontal"
                    months={isMobile ? 1 : 2}
                    showDateDisplay={false}
                    rangeColors={['#1976d2']}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={draftStartTime}
                      onChange={(e) => setDraftStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={draftEndTime}
                      onChange={(e) => setDraftEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        const today = new Date();
                        setDraftDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                        setDraftStartTime('00:00');
                        setDraftEndTime('23:59');
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDatePickerAnchor(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={applyDateRange}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>

            <ClearAllFiltersButton
              onClear={handleClearAllFilters}
              disabled={!hasActiveFilters}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>

          <Box sx={pageFiltersActionsSx}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload />}
              endIcon={<ArrowDropDown />}
              onClick={openActivityExportMenu}
              disabled={activityExportLoading || loading}
              sx={{
                ...pageFilterActionButtonSx,
                minWidth: { xs: '100%', lg: 160 },
              }}
            >
              Export
            </Button>
            <Menu
              anchorEl={activityExportMenuAnchor}
              open={activityExportMenuOpen}
              onClose={closeActivityExportMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleChooseExportFormat('csv')}>Export in CSV</MenuItem>
              <MenuItem onClick={() => handleChooseExportFormat('xlsx')}>Export in XLSX</MenuItem>
            </Menu>
          </Box>
        </Stack>
      </Box>

      {cameraLabelGroups.length > 0 ? (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            useFlexGap
            gap={1}
            sx={{ mb: 1 }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.5 }}
            >
              Detections Per Activity Camera : 
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} alignItems="center">
              {cameraLabelGroups.map((row) => {
                const col = colorForFilterCamera(row.label);
                return (
                  <Chip
                    key={row.label}
                    size="small"
                    label={`${row.label} · ${row.count} (${row.percent}%)`}
                    sx={{
                      fontWeight: 500,
                      border: '1px solid',
                      borderColor: col,
                      color: col,
                      bgcolor: alpha(col, 0.12),
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <Box
            sx={{
              px: '2px',
              py: '3px',
              bgcolor: '#fff',
              borderRadius: 1.25,
              boxSizing: 'border-box',
              boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.35)',
              width: '100%',
              mb: 1.25,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: 12,
                borderRadius: 0.75,
                overflow: 'hidden',
                bgcolor: '#fff',
              }}
            >
              {buildVisibleAnalysisBarSegments(cameraLabelGroups).map((seg, index) => {
                const numericPercent = parseAnalysisPercent(seg?.percent);
                const displayPercent = Math.max(0, Number(seg.displayPercent) || 0);
                const percentLabel = formatAnalysisPercent(seg?.percent);
                const hasValue = numericPercent > 0 || Number(seg?.count) > 0;
                const labelText = String(seg?.label || '').trim() || 'Other';
                return (
                  <Tooltip
                    key={`camera-strip-${labelText}`}
                    title={`${labelText} — ${seg?.count || 0} (${percentLabel})`}
                    placement="top"
                  >
                    <Box
                      sx={{
                        flex: `0 0 ${displayPercent}%`,
                        minWidth: 0,
                        boxSizing: 'border-box',
                        bgcolor: colorForFilterCamera(labelText),
                        boxShadow: index > 0 ? 'inset 2px 0 0 #fff' : 'none',
                        opacity: hasValue ? 1 : 0,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box sx={{ minHeight: 260, position: 'relative' }}>
        <Box
          sx={{
            position: loading ? 'relative' : 'absolute',
            inset: 0,
            opacity: loading ? 1 : 0,
            transform: loading ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 260ms ease, transform 260ms ease',
            pointerEvents: loading ? 'auto' : 'none',
          }}
        >
          <Box sx={activityGridSx}>
            {Array.from({ length: 8 }).map((_, idx) => (
              <Box key={`activity-skel-${idx}`} sx={{ minWidth: 0 }}>
                <ActivitySummaryCardSkeleton />
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            position: loading ? 'absolute' : 'relative',
            inset: 0,
            opacity: loading ? 0 : 1,
            transform: loading ? 'translateY(8px)' : 'translateY(0)',
            transition: 'opacity 300ms ease, transform 300ms ease',
            pointerEvents: loading ? 'none' : 'auto',
          }}
        >
          {filteredGroups.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {violationFilter === 'violation' ? 'No violation logs found' : 'No activity logs found'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adjust filters or widen the day range to see cards.
            </Typography>
          </Paper>
          ) : (
            <Box sx={activityGridSx}>
              {filteredGroups.map((group) => {
                const thumb = group.person_id ? personThumbnails[group.person_id] : null;
                return (
                  <Box key={group.group_key} sx={{ minWidth: 0 }}>
                    <ActivitySummaryCard
                      group={group}
                      thumbnail={thumb}
                      snapshotCache={{}}
                      onOpenTimeline={openTimeline}
                      onOpenDetail={openProcessingDialog}
                      onBecomeVisible={loadGroupPreviewImagesIfNeeded}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {violationFilter === 'violation'
              ? `Showing ${filteredGroups.length} violation people on this page`
              : `Showing ${pagination.total === 0 ? 0 : pagination.offset + 1}-${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${pagination.total} people`}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Cards per page:</Typography>
            <Select
              size="small"
              value={pagination.limit}
              onChange={(e) => loadGroups(parseInt(e.target.value, 10), 0)}
              sx={{ minWidth: 90 }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>
        </Stack>
        <Pagination
          count={pageCount}
          page={pagination.current_page}
          onChange={(e, page) => loadGroups(pagination.limit, (page - 1) * pagination.limit)}
          showFirstButton
          showLastButton
        />
      </Box>

      <Dialog
        open={activityExportDialogOpen}
        onClose={() => setActivityExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography component="div" variant="inherit" sx={{ fontWeight: 600, minWidth: 0 }}>
            Export Activity {activityExportFormat.toUpperCase()}
          </Typography>
          <IconButton
            onClick={() => setActivityExportDialogOpen(false)}
            size="small"
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="File name"
                size="small"
                value={activityExportFileName}
                onChange={(e) => setActivityExportFileName(e.target.value)}
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="activity-export-camera-label">Camera</InputLabel>
                <Select
                  labelId="activity-export-camera-label"
                  label="Camera"
                  value={activityExportCameraId}
                  onChange={(event) => setActivityExportCameraId(event.target.value)}
                >
                  <MenuItem value="">All Cameras</MenuItem>
                  {activityCameras.map((camera) => (
                    <MenuItem key={camera.camera_id || camera.label} value={camera.camera_id || ''}>
                      {camera.label || camera.camera_id || 'Activity camera'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Autocomplete
                size="small"
                freeSolo
                options={safeAvailableNames}
                value={activityExportPersonName || null}
                inputValue={activityExportPersonName || ''}
                onInputChange={(e, newValue) => setActivityExportPersonName(sanitizeNameSearchInput(newValue || ''))}
                renderInput={(params) => <TextField {...params} label="Search by Name" />}
                sx={{ flex: 1 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Timestamp Date Filter"
                  value={
                    `${isSameDay(activityExportDateRange[0].startDate, activityExportDateRange[0].endDate)
                      ? format(activityExportDateRange[0].startDate, 'MMM dd, yyyy')
                      : `${format(activityExportDateRange[0].startDate, 'MMM dd')} - ${format(activityExportDateRange[0].endDate, 'MMM dd, yyyy')}`
                    }  (${activityExportStartTime} - ${activityExportEndTime})`
                  }
                  onClick={(e) => setActivityExportDatePickerAnchor(e.currentTarget)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ readOnly: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonth fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    sx: { cursor: 'pointer' },
                  }}
                />
                <Popover
                  open={activityExportDatePickerOpen}
                  anchorEl={activityExportDatePickerAnchor}
                  onClose={() => setActivityExportDatePickerAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: { maxWidth: '100vw', overflow: 'auto' },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRight: { md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: 'divider',
                        minWidth: 140,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Quick Select
                      </Typography>
                      <Stack spacing={0.5}>
                        {[
                          { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                          { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                          { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                          { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                          { label: 'This Month', getValue: () => {
                            const now = new Date();
                            return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                          } },
                          { label: 'Last Month', getValue: () => {
                            const now = new Date();
                            return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                          } },
                        ].map((preset) => (
                          <Button
                            key={preset.label}
                            size="small"
                            variant="text"
                            onClick={() => {
                              const { startDate, endDate } = preset.getValue();
                              setActivityExportDateRange([{ startDate, endDate, key: 'selection' }]);
                            }}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              px: 1.5,
                              py: 0.75,
                              color: 'text.primary',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            {preset.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        '& .rdrCalendarWrapper': {
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        },
                        '& .rdrMonth': {
                          width: { xs: '280px', sm: '300px' },
                        },
                      }}
                    >
                      <DateRangePicker
                        ranges={activityExportDateRange}
                        onChange={(ranges) => setActivityExportDateRange([ranges.selection])}
                        moveRangeOnFirstSelection={false}
                        months={isMobile ? 1 : 2}
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        rangeColors={['#40518A']}
                        staticRanges={[]}
                        inputRanges={[]}
                        maxDate={new Date()}
                      />
                      <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          size="small"
                          value={activityExportStartTime}
                          onChange={(e) => setActivityExportStartTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                          fullWidth
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          size="small"
                          value={activityExportEndTime}
                          onChange={(e) => setActivityExportEndTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                        <Button
                          onClick={() => {
                            const today = new Date();
                            setActivityExportDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                            setActivityExportStartTime('00:00');
                            setActivityExportEndTime('23:59');
                          }}
                          variant="outlined"
                          size="small"
                          startIcon={<RefreshIcon />}
                        >
                          Reset
                        </Button>
                        <Button onClick={() => setActivityExportDatePickerAnchor(null)} variant="outlined" size="small">
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            const range = buildExportDateTimeRange();
                            if (range?.error) {
                              show(range.error, 'error');
                              return;
                            }
                            setActivityExportDatePickerAnchor(null);
                          }}
                          variant="contained"
                          size="small"
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Popover>
              </Box>
            </Stack>

            <Divider />

            <Typography variant="subtitle2">Columns (select + order)</Typography>
            <List dense sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {(() => {
                const fieldByName = new Map(activityExportVisibleFields.map((field) => [field.field, field]));
                const selectedFieldsInOrder = activityExportSelectedFields
                  .map((fieldName) => fieldByName.get(fieldName))
                  .filter(Boolean);
                const unselectedFields = activityExportVisibleFields.filter(
                  (field) => !activityExportSelectedFields.includes(field.field)
                );
                const orderedFields = [...selectedFieldsInOrder, ...unselectedFields];

                return orderedFields.map((field) => {
                  const selected = activityExportSelectedFields.includes(field.field);
                  const selectedIndex = activityExportSelectedFields.indexOf(field.field);
                  return (
                    <ListItem
                      key={field.field}
                      secondaryAction={selected ? (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => moveSelectedExportField(selectedIndex, 'up')} disabled={selectedIndex <= 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveSelectedExportField(selectedIndex, 'down')}
                            disabled={selectedIndex < 0 || selectedIndex >= activityExportSelectedFields.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selected}
                          onChange={(e) => toggleActivityExportField(field.field, e.target.checked)}
                        />
                      </ListItemIcon>
                      <ListItemText primary={field.label || field.field} />
                    </ListItem>
                  );
                });
              })()}
            </List>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityExportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExportActivityWithConfirm}
            disabled={activityExportLoading || activityExportSelectedFields.length === 0}
          >
            {activityExportLoading ? 'Exporting...' : `Export ${activityExportFormat.toUpperCase()}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ActivityTimelineModal
        open={timelineDialog.open}
        onClose={() => setTimelineDialog((prev) => ({ ...prev, open: false }))}
        group={timelineDialog.group}
        events={timelineDialog.events}
        loading={timelineDialog.loading}
        totalCount={timelineDialog.total}
        analysis={timelineDialog.analysis}
        analysisLoading={timelineDialog.analysisLoading}
        snapshotCache={{}}
        thumbnail={timelineDialog.group?.person_id ? personThumbnails[timelineDialog.group.person_id] : null}
        onOpenEventDetail={openProcessingDialog}
        onRequestAuditImage={loadAuditImageIfNeeded}
      />

      <AuditProcessingTimelineModal
        timingDialog={processingDialog}
        closeTimingDialog={closeProcessingDialog}
        isMobile={isMobile}
        timingDialogResultMeta={getTimingDialogResultMeta(processingDialog.log)}
        hasTimingDialogTrackAttendance={false}
        modalFrames={Array.isArray(processingDialog.stack) ? processingDialog.stack : []}
        imageCache={{}}
        ANONYMOUS_IMAGE={ANONYMOUS_IMAGE}
        showPossibleMatchActions={false}
        showProcessingTimeline={false}
        showTimingHeading={false}
        showTopMatches={false}
        title="Activity details"
      />
    </Container>
  );
};

export default ActivityLogs;
